import { useState } from 'react';
import { Search, Calendar, Bell, Settings, Plus, Sun, Moon, LogOut } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils.js';
import { endSession, tokenBadge } from '@/lib/session.js';
import useMe from '@/hooks/useMe.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.jsx';
import { getTheme, toggleTheme } from '@/lib/theme.js';
import { toast } from '@/components/ui/toaster.jsx';
import { useShellStore } from '@/stores/shell-store.js';
import NewIntakeSheet from '@/components/intake/NewIntakeSheet.jsx';

// Radar mark — concentric rings + cross ticks (TRACON-DESIGN-SYSTEM.md, the
// `i-tracon` glyph from the mockups). Crimson; sits beside the cream wordmark.
function RadarMark({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <line x1="12" y1="3" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="3" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="21" y2="12" />
    </svg>
  );
}

// Topbar section tabs. Routed entries navigate; placeholders toast until their
// surface ships (Task I is the shell only — these light up as routes land).
const TABS = [
  { label: 'Pipeline', to: '/pipeline' },
  { label: 'Inbox', soon: true },
  { label: 'Quotes', soon: true },
  { label: 'Contacts', to: '/search' },
  { label: 'Trust', soon: true },
  { label: 'Admin', to: '/admin' },
];

function openSearch() {
  window.dispatchEvent(new Event('search-palette:open'));
}

export default function TopBar() {
  const navigate = useNavigate();
  const navCollapsed = useShellStore((s) => s.navCollapsed);
  const badge = tokenBadge(useMe());
  async function signOut() {
    await endSession();
    navigate('/login', { replace: true });
  }
  // MV-01 3.1 (Ship 1b): global "+ New intake" — deal-first broker intake.
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme);

  return (
    <header
      className="flex h-topbar-h shrink-0 items-center gap-3 border-b border-border bg-surface px-3"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Brand — aligns with the left-nav column; collapses with it. */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className={cn(
          'flex shrink-0 items-center gap-2 transition-[width] duration-200',
          navCollapsed ? 'md:w-nav-w-closed' : 'md:w-nav-w'
        )}
      >
        <span className="grid h-[22px] w-[22px] place-items-center text-accent">
          <RadarMark size={18} />
        </span>
        <span
          className={cn(
            'text-[13px] font-bold tracking-[0.06em] text-cream',
            navCollapsed && 'md:hidden'
          )}
        >
          TRACON
        </span>
      </button>

      {/* cmd-K search — full box on desktop, icon button on mobile. */}
      <button
        type="button"
        onClick={openSearch}
        aria-label="Search (⌘K)"
        className="hidden max-w-[380px] flex-1 items-center gap-2 rounded-[5px] border border-border bg-bg px-2.5 py-[5px] text-xs text-text-muted hover:border-border-strong md:flex"
      >
        <Search size={14} strokeWidth={1.5} className="text-text-dim" />
        <span>Jump to a deal, pilot, N-number…</span>
        <kbd className="ml-auto rounded-[3px] border border-border bg-surface-strong px-1.5 py-px font-mono text-[10px] text-text-dim">
          ⌘K
        </kbd>
      </button>

      {/* Section tabs — desktop only (mobile uses the bottom tab bar). */}
      <nav className="ml-1 hidden items-center gap-px lg:flex">
        {TABS.map((tab) =>
          tab.to ? (
            <NavLink
              key={tab.label}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  'rounded px-2.5 py-1.5 text-xs',
                  isActive
                    ? 'bg-surface-strong text-cream'
                    : 'text-text-muted hover:text-text'
                )
              }
            >
              {tab.label}
            </NavLink>
          ) : (
            <button
              key={tab.label}
              type="button"
              onClick={() => toast(`${tab.label} — coming soon`)}
              className="rounded px-2.5 py-1.5 text-xs text-text-muted hover:text-text"
            >
              {tab.label}
            </button>
          )
        )}
      </nav>

      {/* Right cluster */}
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={() => setIntakeOpen(true)}
          className="mr-1 flex h-7 items-center gap-1 rounded border border-border px-2 text-xs font-medium text-text-muted hover:bg-surface-strong hover:text-text"
        >
          <Plus size={13} strokeWidth={2} />
          <span className="hidden sm:inline">New intake</span>
        </button>
        <NewIntakeSheet open={intakeOpen} onOpenChange={setIntakeOpen} />
        <button
          type="button"
          aria-label="Search"
          onClick={openSearch}
          className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text md:hidden"
        >
          <Search size={16} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Calendar"
          onClick={() => toast('Calendar — week ahead across all deals (coming soon)')}
          className="hidden h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text sm:grid"
        >
          <Calendar size={16} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Notifications"
          onClick={() => toast('Notifications (coming soon)')}
          className="relative grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
        >
          <Bell size={16} strokeWidth={1.5} />
          <span className="absolute right-[5px] top-[5px] h-1.5 w-1.5 rounded-full bg-accent ring-2 ring-surface" />
        </button>
        <button
          type="button"
          aria-label={theme === 'dark' ? 'Switch to day' : 'Switch to night'}
          title={theme === 'dark' ? 'Day' : 'Night'}
          onClick={() => setTheme(toggleTheme())}
          className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
          data-theme-toggle
        >
          {theme === 'dark' ? <Sun size={16} strokeWidth={1.5} /> : <Moon size={16} strokeWidth={1.5} />}
        </button>
        <button
          type="button"
          aria-label="Settings"
          onClick={() => toast('Settings (coming soon)')}
          className="hidden h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text sm:grid"
        >
          <Settings size={16} strokeWidth={1.5} />
        </button>
        {/* Whose sign-in this is (GET /auth/me, personal-logins M11) and Sign out. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account"
              title={badge ? badge.title : undefined}
              className="ml-1 flex items-center gap-1.5 rounded-full text-text-muted hover:text-text"
            >
              {badge && (
                <span
                  className={cn(
                    'hidden max-w-[160px] truncate rounded-full border px-2 py-0.5 text-[11px] sm:inline',
                    badge.kind === 'shared' ? 'border-accent/40 text-accent' : 'border-border text-text-muted'
                  )}
                >
                  {badge.label}
                </span>
              )}
              <span className="grid h-[26px] w-[26px] place-items-center rounded-full bg-border-strong text-[11px] font-semibold text-cream">
                {badge ? badge.initials : '?'}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="font-normal">
              <span className="block text-sm text-text">{badge ? badge.label : 'Signed in'}</span>
              {badge && <span className="block text-xs text-text-dim">{badge.kind === 'shared' ? 'Who you are is not recorded' : badge.title}</span>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={signOut}>
              <LogOut size={14} strokeWidth={1.5} className="mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
